import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { count } from '../manage/manage';
import { CampaignChoice, CampaignSelect } from '../outreach/Pickers';
import { CRM_EMAIL_AUDIENCE, CRM_SCHEDULE_EMAIL_CAMPAIGN, CRM_SEND_DELIVERY_TEST, CRM_SEND_TEST_EMAIL } from './email.queries';
import { Campaign, changedCount, describeEmailError, errorCode, parseTestRecipients } from './emailModel';

const useNarrow = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

/* ------------------------------------------------------------------ *
 * A test
 * ------------------------------------------------------------------ */

export const TestSendDialog = ({ campaignId, onClose }: { campaignId: number; onClose: () => void }) => {
  const phone = useNarrow();
  const [typed, setTyped] = useState('');
  const [tried, setTried] = useState(false);
  const [send, { data, loading, error }] = useMutation<{ crmSendTestEmail: TestSent }>(CRM_SEND_TEST_EMAIL);
  const parsed = parseTestRecipients(typed);
  const result = data?.crmSendTestEmail;

  const go = async () => {
    setTried(true);
    if (parsed.problem) return;
    try {
      await send({ variables: { id: campaignId, to: parsed.to } });
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-test-title">
      <DialogTitle id="crm-test-title">Send a test</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          It goes as saved, with “[Test]” before the subject and the merge fields filled with your own name. Its unsubscribe link works but unsubscribes nobody.
        </Typography>
        <TextField
          label="Send to"
          placeholder="you@example.org, a colleague@example.org"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          error={tried && !!parsed.problem}
          helperText={(tried && parsed.problem) || 'Up to 5 addresses.'}
          autoFocus
          disabled={loading}
        />
        {result && <TestResult result={result} />}
        {error && <Alert severity="error">{describeEmailError(error, 'The test could not be sent.')}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={loading}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        <Button variant="contained" onClick={go} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {result ? 'Send again' : 'Send test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type TestSent = { sentTo: Array<string>; dryRun: boolean };

/** Where a test went, or that this server only logged it. */
const TestResult = ({ result }: { result: TestSent }) => (
  <Alert severity={result.dryRun ? 'warning' : 'success'}>
    {result.dryRun ? (
      <>
        <AlertTitle>Logged, not delivered</AlertTitle>
        This server’s API only logged it (a dry run), so nothing will arrive. Deploy the latest API: it delivers tests on every server.
      </>
    ) : (
      `Sent to ${result.sentTo.join(', ')}. It should arrive within a minute.`
    )}
  </Alert>
);

/* ------------------------------------------------------------------ *
 * A test from the Email page: a check message, or any campaign
 * ------------------------------------------------------------------ */

type TestKind = 'check' | 'campaign';

/**
 * Type an address and send a test. By default a short fixed message
 * (`crmSendDeliveryTest`), which needs no campaign and shows email from the
 * CRM arrives; or one of our campaigns as it stands (`crmSendTestEmail`).
 */
export const TestEmailDialog = ({ onClose }: { onClose: () => void }) => {
  const phone = useNarrow();
  const [typed, setTyped] = useState('');
  const [kind, setKind] = useState<TestKind>('check');
  const [campaign, setCampaign] = useState<CampaignChoice | null>(null);
  const [tried, setTried] = useState(false);
  const [sendCheck, check] = useMutation<{ crmSendDeliveryTest: TestSent }>(CRM_SEND_DELIVERY_TEST);
  const [sendCampaign, ofCampaign] = useMutation<{ crmSendTestEmail: TestSent }>(CRM_SEND_TEST_EMAIL);
  const parsed = parseTestRecipients(typed);
  const shown = kind === 'check' ? check : ofCampaign;
  const result = kind === 'check' ? check.data?.crmSendDeliveryTest : ofCampaign.data?.crmSendTestEmail;
  const loading = check.loading || ofCampaign.loading;
  const campaignProblem = kind === 'campaign' && !campaign ? 'Choose the campaign to send.' : undefined;

  const go = async () => {
    setTried(true);
    if (parsed.problem || campaignProblem) return;
    try {
      if (kind === 'check') await sendCheck({ variables: { to: parsed.to } });
      else if (campaign) await sendCampaign({ variables: { id: campaign.id, to: parsed.to } });
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-test-email-title">
      <DialogTitle id="crm-test-email-title">Send a test email</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <TextField
          label="Send to"
          placeholder="you@example.org"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          error={tried && !!parsed.problem}
          helperText={(tried && parsed.problem) || 'Up to 5 addresses, separated by commas.'}
          inputProps={{ inputMode: 'email' }}
          autoFocus
          disabled={loading}
        />
        <Box>
          <Typography variant="subtitle2" component="p" id="crm-test-kind" sx={{ mb: 0.75 }}>
            What to send
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={kind}
            onChange={(_, k) => k && setKind(k)}
            disabled={loading}
            aria-labelledby="crm-test-kind"
          >
            <ToggleButton value="check">A check message</ToggleButton>
            <ToggleButton value="campaign">A campaign</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {kind === 'check' ? (
          <Typography variant="body2" color="text.secondary">
            A short message saying it is a test, from the dashboard’s usual sender, with “[Test]” in the subject. Use it to check email from the CRM arrives.
          </Typography>
        ) : (
          <>
            <CampaignSelect anyStatus value={campaign} onChange={setCampaign} error={tried ? campaignProblem : undefined} disabled={loading} />
            <Typography variant="body2" color="text.secondary">
              It goes as saved, with “[Test]” before the subject and the merge fields filled with your own name. Its unsubscribe link works but unsubscribes nobody.
            </Typography>
          </>
        )}
        {result && <TestResult result={result} />}
        {shown.error && <Alert severity="error">{describeEmailError(shown.error, 'The test could not be sent.')}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={loading}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        <Button variant="contained" onClick={go} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {result ? 'Send again' : 'Send test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ *
 * Scheduling, with the number confirmed
 * ------------------------------------------------------------------ */

interface ScheduleDialogProps {
  campaign: Pick<Campaign, 'id' | 'name' | 'segmentId' | 'segmentName'>;
  onClose: () => void;
  onScheduled: (campaign: Campaign) => void;
}

const inputFormat = 'YYYY-MM-DDTHH:mm';

/**
 * Send now, or at a time within a year. The number on the button is
 * `crmEmailAudience.canEmail`, counted when the dialog opens and sent back as
 * `expectedCount`: on any other number the API schedules nothing, and the
 * dialog then shows the new one to confirm.
 */
export const ScheduleDialog = ({ campaign, onClose, onScheduled }: ScheduleDialogProps) => {
  const phone = useNarrow();
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [at, setAt] = useState(() => dayjs().add(1, 'day').hour(10).minute(0).format(inputFormat));
  const [expected, setExpected] = useState<number | null>(null);
  const [countAudience, audience] = useLazyQuery<{ crmEmailAudience: { total: number; canEmail: number } }>(CRM_EMAIL_AUDIENCE, { fetchPolicy: 'network-only' });
  const [schedule, scheduling] = useMutation<{ crmScheduleEmailCampaign: Campaign }>(CRM_SCHEDULE_EMAIL_CAMPAIGN);

  useEffect(() => {
    if (campaign.segmentId) countAudience({ variables: { segmentId: campaign.segmentId } }).then(({ data }) => data && setExpected(data.crmEmailAudience.canEmail));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.segmentId]);

  const sendAt = when === 'later' ? dayjs(at) : null;
  const timeProblem =
    sendAt && (!sendAt.isValid() ? 'Choose a date and time.' : sendAt.isBefore(dayjs()) ? 'That time has passed.' : sendAt.isAfter(dayjs().add(1, 'year')) ? 'At most a year ahead.' : undefined);

  const go = async () => {
    if (expected === null || timeProblem) return;
    try {
      const { data } = await schedule({ variables: { id: campaign.id, expectedCount: expected, sendAt: sendAt ? sendAt.toISOString() : null } });
      if (data) onScheduled(data.crmScheduleEmailCampaign);
    } catch (e) {
      const now = changedCount(e);
      if (now !== undefined) setExpected(now);
    }
  };

  const disabled = errorCode(scheduling.error) === 'CRM_SENDING_DISABLED';

  return (
    <Dialog open onClose={scheduling.loading ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-schedule-title">
      <DialogTitle id="crm-schedule-title">Send {campaign.name}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ p: 2, borderRadius: 2.5, textAlign: 'center', background: 'linear-gradient(135deg, rgba(79,70,229,.08), rgba(124,58,237,.06))' }}>
          {expected === null ? (
            audience.error ? (
              <Typography color="error">Could not count who it reaches.</Typography>
            ) : (
              <CircularProgress size={24} aria-label="Counting" />
            )
          ) : (
            <>
              <Typography sx={{ fontSize: '2.25rem', fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{count(expected)}</Typography>
              <Typography variant="body2">
                {expected === 1 ? 'person' : 'people'} on {campaign.segmentName ?? 'the list'} can be emailed
              </Typography>
              {audience.data && audience.data.crmEmailAudience.total !== expected && (
                <Typography variant="caption" color="text.secondary">
                  {count(audience.data.crmEmailAudience.total)} are on it; the rest are not opted in, have no good address, or are do-not-contact.
                </Typography>
              )}
            </>
          )}
        </Box>

        <ToggleButtonGroup exclusive fullWidth size="small" value={when} onChange={(_, w) => w && setWhen(w)} disabled={scheduling.loading}>
          <ToggleButton value="now">Send now</ToggleButton>
          <ToggleButton value="later">At a time</ToggleButton>
        </ToggleButtonGroup>
        {when === 'later' && (
          <TextField
            type="datetime-local"
            label="Send at (your time)"
            value={at}
            onChange={(event) => setAt(event.target.value)}
            error={!!timeProblem}
            helperText={timeProblem ?? dayjs(at).format('dddd D MMMM YYYY, h:mm a')}
            InputLabelProps={{ shrink: true }}
          />
        )}
        <Typography variant="caption" color="text.secondary">
          It starts within a minute of its time and goes out in steady batches, a set number each minute. Someone who joins the list after it starts does not
          get it. You can unschedule it until it starts, and cancel what has not gone while it sends.
        </Typography>

        {scheduling.error && (
          <Alert severity={disabled ? 'info' : changedCount(scheduling.error) !== undefined ? 'warning' : 'error'}>
            {disabled && <AlertTitle>Sending is switched off here</AlertTitle>}
            {describeEmailError(scheduling.error, 'It could not be scheduled.')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={scheduling.loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={go}
          disabled={expected === null || expected === 0 || !!timeProblem || scheduling.loading}
          startIcon={scheduling.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {expected === null ? 'Counting…' : when === 'now' ? `Send to ${count(expected)} now` : `Schedule for ${count(expected)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
